import { useCallback, useEffect, useState } from 'react';
import { api, type ScheduleDecisionView, type ScheduleOutcome, type SchedulePlan } from './api.ts';
import './schedule.css';

/**
 * Scheduled re-assessments: what the next sweep would do, and why.
 *
 * A one-off scan tells an MSP how a client tenant looked on the day somebody
 * clicked. Evidence an auditor accepts is a cadence, and the risk a cadence
 * carries is that it stops running for one customer without anybody noticing —
 * a revoked consent, a pause set during a migration and never cleared, a tenant
 * failing every attempt. So this screen is not a list of what is scheduled; it
 * is the list of every tenant, each with the sentence explaining its own
 * outcome, ordered so that the tenants nothing is happening to are impossible
 * to overlook.
 *
 * **The sentences are not written here.** Every `detail` comes from the
 * scheduler and is rendered verbatim. The API, the CLI and this screen all show
 * the same string, which is the only way three surfaces can agree about why a
 * customer was or was not scanned — and the console has no scheduling rule of
 * its own to fall out of step with.
 */

/**
 * Skipped tenants, most actionable first.
 *
 * Backing off and in flight are transient and self-correcting; not due is the
 * healthy majority; paused and not connected are the two that need a person,
 * and they sit last only because they are the states an operator already knows
 * about — everything above them is the machine's own behaviour.
 */
const SKIP_ORDER: readonly ScheduleOutcome[] = [
  'backing_off',
  'scan_in_flight',
  'not_due',
  'paused',
  'not_connected',
];

/**
 * How loudly each group is drawn.
 *
 * Nothing on this screen is a verdict, so nothing on it is red. Backing off is
 * the one outcome the machine cannot resolve on its own account — repeated
 * failures against one tenant — so it is the only amber. Paused and not
 * connected are recessed rather than hidden: they are states an operator chose
 * or already knows about, and they still have to be countable at a glance.
 */
const TONE: Partial<Record<ScheduleOutcome, 'attention' | 'quiet'>> = {
  backing_off: 'attention',
  paused: 'quiet',
  not_connected: 'quiet',
};

const message = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const Schedule = () => {
  const [plan, setPlan] = useState<SchedulePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  /**
   * A screen reader does not re-announce a live region whose text is unchanged,
   * and refreshing twice in a row is ordinary here. The alternating zero-width
   * space guarantees a change without a visible one.
   */
  const announce = useCallback((text: string) => {
    setAnnouncement((previous) => (previous.endsWith('\u200B') ? text : `${text}\u200B`));
  }, []);

  const load = useCallback(
    async (announceResult: boolean) => {
      setBusy(true);
      try {
        const next = await api.getSchedule();
        setPlan(next);
        setError(null);
        if (announceResult) {
          announce(`Plan refreshed. ${plural(next.enqueue.length, 'tenant')} due on the next sweep.`);
        }
      } catch (cause) {
        setError(message(cause));
      } finally {
        setBusy(false);
        setLoading(false);
      }
    },
    [announce],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const total =
    plan === null ? 0 : plan.enqueue.length + plan.deferred.length + plan.skipped.length;

  return (
    <div className="schedule-screen">
      <p className="lead">
        DriftWatch re-assesses each connected tenant on a cadence, so a client’s evidence stays
        current without anybody remembering to run it. This is where you can see what that sweep
        would do next, and where you can find the tenants it is leaving alone.
      </p>

      <p aria-live="polite" role="status" className="sr-only">
        {announcement}
      </p>

      {/* Load-bearing rather than decorative: a preview that could be mistaken
          for a trigger would make an operator afraid to open the screen at all,
          and the screen only earns its place if it is opened. */}
      <div className="callout sched-notice">
        <span className="callout-title">Nothing on this screen starts a scan</span>
        This is a preview of what the next sweep would do, worked out from the same facts the sweep
        itself reads. Opening the screen, or refreshing it, queues nothing.
      </div>

      {error && <div className="error">{error}</div>}

      {loading ? (
        <p className="note">Loading…</p>
      ) : plan === null ? null : total === 0 ? (
        <div className="empty">
          <span className="empty-title">No client tenants yet, so there is nothing to schedule</span>
          <p className="empty-body">
            The sweep works from the list of connected tenants. Add clients on the Clients screen
            and this one will start describing what happens to each of them.
          </p>
        </div>
      ) : (
        <>
          <div className="toolbar sched-toolbar">
            <p className="note sched-summary">
              {plural(total, 'tenant')} · {plural(plan.enqueue.length, 'tenant')} due on the next
              sweep · up to <span className="num">{plan.maxPerTick}</span> queued per sweep · default
              cadence <span className="num">{plan.defaultCadenceDays}</span> days · worked out as at{' '}
              {new Date(plan.now).toLocaleString('en-GB')}.
            </p>
            <span className="spacer" />
            <button
              className="btn btn-quiet"
              aria-busy={busy || undefined}
              disabled={busy}
              onClick={() => void load(true)}
            >
              {busy ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          <Group
            heading="Due on the next sweep"
            note="These are queued first, longest overdue first."
            rows={plan.enqueue}
          />

          <Group
            heading="Due, held for a later sweep"
            note={`Also due, but beyond the ${plan.maxPerTick} a single sweep queues — Microsoft throttles per tenant, and one slow customer must not become a backlog for the rest. Nothing here is dropped; it is queued on a following sweep.`}
            rows={plan.deferred}
          />

          {SKIP_ORDER.map((outcome) => {
            const rows = plan.skipped.filter((row) => row.outcome === outcome);
            const first = rows[0];
            if (!first) return null;
            return <Group key={outcome} heading={first.label} rows={rows} tone={TONE[outcome]} />;
          })}
        </>
      )}
    </div>
  );
};

interface GroupProps {
  heading: string;
  note?: string;
  rows: ScheduleDecisionView[];
  tone?: 'attention' | 'quiet';
}

const Group = ({ heading, note, rows, tone }: GroupProps) => {
  if (rows.length === 0) return null;

  return (
    <section className={tone ? `card sched-group sched-${tone}` : 'card sched-group'}>
      {/* The count sits in the head rather than in the sentence: "Paused" and
          "Paused, 6" are different facts, and the second is the one an operator
          came here to find. */}
      <div className="card-head">
        <h2>{heading}</h2>
        <span className="sched-count num">{rows.length}</span>
      </div>
      <div className="card-body">
        {note && <p className="note sched-note">{note}</p>}
        <ul className="sched-list">
          {rows.map((row) => (
            <li key={row.tenantId} className="sched-row">
              <span className="sched-name">{row.displayName}</span>
              <span className="note sched-cadence num">
                {row.cadenceDays}-day cadence
                {row.schedule.cadenceDays === null && ' (deployment default)'}
              </span>
              <p className="note sched-detail">{row.detail}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
